import { z } from "zod";

// Schemas for the 'routing' section, aligned with the documentation.
const routingResolvedOrgSchema = z
  .object({
    id: z.number().optional(),
    name: z.string().optional(),
    type: z.string().optional(),
  })
  .passthrough();

export const routingUserSchema = z
  .object({
    id: z.number(),
    sub: z.string().nullish(),
    email: z.string(),
    name: z.string(),
    job_grade: z.number(),
    default_org_id: z.number(),
    created_at: z.string(),
    updated_at: z.union([z.string(), z.object({})]),
    resolved_default_org: routingResolvedOrgSchema.nullish(),
  })
  .passthrough();

const routingApprovalStatusSchema = z.preprocess(
  (value) => {
    if (typeof value === "string") {
      return value.toUpperCase();
    }
    return value;
  },
  z.enum(["WAITING", "PENDING", "APPROVED", "REJECTED", "CANCELLED", "TBD"]),
);

const routingEventStatusSchema = z.preprocess((value) => {
  if (typeof value === "string") {
    return value.toUpperCase();
  }
  return value;
}, z.string());

const routingNodeEventSchema = z.object({
  eventId: z.string(),
  name: z.string(),
  status: routingEventStatusSchema,
});

const routingNodeEventsSchema = z
  .object({
    nodeEnter: z.array(routingNodeEventSchema).optional(),
    nodeLeave: z.array(routingNodeEventSchema).optional(),
  })
  .optional();

const routingTaskEventsSchema = z
  .object({
    taskEnter: z.array(routingNodeEventSchema).optional(),
    taskLeave: z.array(routingNodeEventSchema).optional(),
  })
  .optional();

export const routingApprovalSchema = z.object({
  approvalTaskId: z.string(),
  assignee: routingUserSchema.nullable(),
  status: routingApprovalStatusSchema,
  subGroupIndex: z.number().optional(),
  sub_group_index: z.number().optional(),
  events: routingTaskEventsSchema,
});

export const approvalGroupSchema = z.object({
  approvals: z.array(routingApprovalSchema),
  isReportingLine: z.boolean(),
  desc: z.string().optional(),
  requiresAllApprovals: z.boolean().optional(),
  events: routingNodeEventsSchema,
});

export const reportingLineConfigSchema = z.object({
  type: z.enum(["level", "job_grade"]),
  target: z.number(),
});

const nodeStatusSchema = z.preprocess(
  (value) => {
    if (typeof value === "string") {
      return value.toUpperCase();
    }
    return value;
  },
  z.enum(["INACTIVE", "PENDING", "COMPLETED", "FAILED", "TBD", "ERROR"]),
);

export const baseNodeSchema = z.object({
  key: z.string(),
  status: nodeStatusSchema,
  desc: z.string().optional(),
  parent_keys: z.array(z.string()),
  child_keys: z.array(z.string()).optional(),
  errorMessage: z.string().optional(),
  error_message: z.string().optional(),
  events: routingNodeEventsSchema,
});

export const approvalRoutingNodeSchema = baseNodeSchema.extend({
  type: z.literal("approval"),
  result: z.enum(["approved", "rejected", "delegated", "timeout"]).optional(),
  approvalMethod: z.enum(["single", "parallel"]),
  approvalLogic: z.enum(["AND", "OR"]).optional(),
  reportingLineConfig: reportingLineConfigSchema.optional(),
  approvalGroups: z.array(approvalGroupSchema),
});

export const routingNodeSchema = z.discriminatedUnion("type", [
  approvalRoutingNodeSchema,
  baseNodeSchema.extend({ type: z.literal("start") }),
  baseNodeSchema.extend({ type: z.literal("condition") }),
  baseNodeSchema.extend({ type: z.literal("subflow") }),
  baseNodeSchema.extend({ type: z.literal("end") }),
]);

export const routingSchema = z.object({
  serialNumber: z.string(),
  workflowInstanceId: z.string(),
  nodes: z.array(routingNodeSchema),
});
